import type { BookingDetailsExtrasGroup } from '../../domain/types'
import type { BookingDetailsServiceAPIResponse } from '../types'
import { resolveExtraIconGlyph } from './resolveExtraIconGlyph'

const SPECIAL_LUGGAGE_GROUP_CODE_PREFIX = 'SPECBAG'

export interface BookingDetailsExtrasMapperResult {
  extras: BookingDetailsExtrasGroup[]
  specialLuggageExtras: BookingDetailsExtrasGroup[]
}

function isSpecialLuggageService(service: BookingDetailsServiceAPIResponse): boolean {
  return service.groupCode?.trim().startsWith(SPECIAL_LUGGAGE_GROUP_CODE_PREFIX) ?? false
}

function mapServicesToExtrasGroups(
  services: BookingDetailsServiceAPIResponse[],
): BookingDetailsExtrasGroup[] {
  const groupMap = new Map<string, BookingDetailsExtrasGroup>()

  for (const service of services) {
    const title = (service.title || service.type)?.trim()
    const description = service.description?.trim()

    if (title && description) {
      const quantity = service.quantity ?? (service.participantIds?.length || 1)

      let group = groupMap.get(title)
      if (!group) {
        group = {
          label: title,
          icon: resolveExtraIconGlyph(service.type, service.groupCode),
          items: [],
          isEditable: false,
          selfServiceServiceType: undefined,
        }
        groupMap.set(title, group)
      }
      // Extra's from the same group should have the same selfServiceServiceType, so we only set it when it is not already set.
      if (!group.isEditable && service.isEditable === true) {
        group.isEditable = true
        group.selfServiceServiceType = service.selfServiceServiceType || undefined
      }
      const existingItem = group.items.find(item => item.description === description)
      if (existingItem) {
        existingItem.quantity += quantity
      }
      else {
        group.items.push({ description, quantity })
      }
    }
  }

  return Array.from(groupMap.values())
}

export function extrasMapper(
  services: BookingDetailsServiceAPIResponse[] | undefined,
): BookingDetailsExtrasMapperResult {
  const allServices = services ?? []
  const regularServices = allServices.filter(service => !isSpecialLuggageService(service))
  const specialLuggageServices = allServices.filter(isSpecialLuggageService)

  return {
    extras: mapServicesToExtrasGroups(regularServices),
    specialLuggageExtras: mapServicesToExtrasGroups(specialLuggageServices),
  }
}
